import { useState, type ReactElement } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { Download, Plus, Search, Upload } from 'lucide-react'
import { z } from 'zod'

import {
  BulkImportTemplatesDialog,
  CoaTemplatesTable,
  coaTemplatesListQuery,
  downloadCoaTemplatesExport,
  useBulkImportExecute,
  useBulkImportPreview,
  useDeleteCoaTemplate,
  type CoaTemplate,
} from '~/features/coa-templates'
import { LegacyModalSurface } from '~/components/overlays/ModalSurface'
import { COUNTRIES } from '~/lib/countries'
import { useUrlState } from '~/lib/query/use-url-state'
import type { AppError } from '~/lib/result'

/**
 * `/admin/finance/reference-accounts` — Chart-of-Accounts templates list.
 * One row per country template; opening a row goes to its account list.
 */
const searchSchema = z.object({
  country: z.string().length(2).optional(),
  search: z.string().optional(),
})
const URL_DEFAULTS = {}

export const Route = createFileRoute('/admin/finance/reference-accounts/')({
  validateSearch: searchSchema,
  component: ReferenceAccountsPage,
})

function ReferenceAccountsPage(): ReactElement {
  const navigate = useNavigate()
  const [search, setUrl] = useUrlState(searchSchema, URL_DEFAULTS)
  const [searchInput, setSearchInput] = useState(search.search ?? '')
  const [deleteTarget, setDeleteTarget] = useState<CoaTemplate | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [bulkImportOpen, setBulkImportOpen] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [exportError, setExportError] = useState<string | null>(null)
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const listQuery = useQuery(
    coaTemplatesListQuery({ countryCode: search.country, search: search.search }),
  )
  const deleteMutation = useDeleteCoaTemplate()
  const bulkImportPreview = useBulkImportPreview()
  const bulkImportExecute = useBulkImportExecute()

  const templates = listQuery.data?.templates ?? []
  const hasFilters = Boolean(search.country || search.search)

  return (
    <div className="ka-page">
      <header className="ka-page-header">
        <div>
          <h1 className="ka-page-header-title">Chart of Accounts Templates</h1>
          <p className="ka-page-header-subtitle">
            {listQuery.data?.total ?? templates.length} templates
          </p>
        </div>
        <div className="ka-page-header-actions">
          <button
            type="button"
            className="ka-btn ka-btn--secondary"
            disabled={!selectedId || isExporting}
            title={selectedId ? undefined : 'Tick one template to export'}
            onClick={() => {
              if (!selectedId) return
              setExportError(null)
              setIsExporting(true)
              downloadCoaTemplatesExport(selectedId)
                .catch((cause: unknown) => setExportError(errorToMessage(cause)))
                .finally(() => setIsExporting(false))
            }}
          >
            <Download size={14} aria-hidden="true" /> {isExporting ? 'Exporting…' : 'Export'}
          </button>
          <button
            type="button"
            className="ka-btn ka-btn--secondary"
            onClick={() => {
              bulkImportPreview.reset()
              bulkImportExecute.reset()
              setBulkImportOpen(true)
            }}
          >
            <Upload size={14} aria-hidden="true" /> Import
          </button>
          <button
            type="button"
            className="ka-btn ka-btn--primary"
            onClick={() => void navigate({ to: '/admin/finance/reference-accounts/new' })}
          >
            <Plus size={14} aria-hidden="true" /> New Template
          </button>
        </div>
      </header>

      {exportError ? (
        <div className="ka-banner is-error" role="alert" aria-live="assertive" style={{ marginBottom: 12 }}>
          {exportError}
        </div>
      ) : null}

      <div className="ka-filter-strip" role="search">
        <label className="ka-filter-search" aria-label="Search templates by name">
          <Search size={14} aria-hidden="true" />
          <input
            type="search"
            placeholder="Search name…"
            value={searchInput}
            onChange={(event) => {
              setSearchInput(event.target.value)
              setUrl({ search: event.target.value || undefined })
            }}
          />
        </label>

        <div className="ka-field-input-wrap" style={{ maxWidth: 220 }}>
          <select
            className="ka-field-input"
            value={search.country ?? ''}
            onChange={(event) => {
              setUrl({ country: event.target.value || undefined })
            }}
          >
            <option value="">All countries</option>
            {COUNTRIES.map((country) => (
              <option key={country.code} value={country.code}>
                {country.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <CoaTemplatesTable
        templates={templates}
        isLoading={listQuery.isPending}
        error={listQuery.error}
        onRowClick={(template) =>
          void navigate({
            to: '/admin/finance/reference-accounts/$templateId',
            params: { templateId: template.id },
          })
        }
        onEdit={(template) =>
          void navigate({
            to: '/admin/finance/reference-accounts/$templateId/edit',
            params: { templateId: template.id },
          })
        }
        onDelete={(template) => {
          setDeleteError(null)
          setDeleteTarget(template)
        }}
        onCreate={() => void navigate({ to: '/admin/finance/reference-accounts/new' })}
        hasFilters={hasFilters}
        onClearFilters={() => {
          setSearchInput('')
          setUrl({ country: undefined, search: undefined })
        }}
        selectedId={selectedId}
        onSelect={setSelectedId}
      />

      {deleteTarget ? (
        <ConfirmDeleteDialog
          template={deleteTarget}
          isSubmitting={deleteMutation.isPending}
          serverError={deleteError}
          onClose={() => setDeleteTarget(null)}
          onConfirm={() => {
            const deletedId = deleteTarget.id
            deleteMutation.mutate(deletedId, {
              onSuccess: () => {
                setDeleteTarget(null)
                setDeleteError(null)
                setSelectedId((current) => (current === deletedId ? null : current))
              },
              onError: (cause) => setDeleteError(errorToMessage(cause)),
            })
          }}
        />
      ) : null}

      {bulkImportOpen ? (
        <BulkImportTemplatesDialog
          isPreviewing={bulkImportPreview.isPending}
          isImporting={bulkImportExecute.isPending}
          preview={bulkImportPreview.data ?? null}
          summary={bulkImportExecute.data ?? null}
          serverError={
            bulkImportPreview.error
              ? errorToMessage(bulkImportPreview.error)
              : bulkImportExecute.error
                ? errorToMessage(bulkImportExecute.error)
                : null
          }
          onClose={() => {
            setBulkImportOpen(false)
            bulkImportPreview.reset()
            bulkImportExecute.reset()
          }}
          onPreview={(file) => bulkImportPreview.mutate(file)}
          onConfirm={(validTemplates, validAccounts) =>
            bulkImportExecute.mutate({
              templates: validTemplates as Parameters<typeof bulkImportExecute.mutate>[0]['templates'],
              accounts: validAccounts as Parameters<typeof bulkImportExecute.mutate>[0]['accounts'],
            })
          }
          onReset={() => bulkImportPreview.reset()}
        />
      ) : null}
    </div>
  )
}

function ConfirmDeleteDialog({
  template,
  isSubmitting,
  serverError,
  onClose,
  onConfirm,
}: {
  readonly template: CoaTemplate
  readonly isSubmitting: boolean
  readonly serverError: string | null
  readonly onClose: () => void
  readonly onConfirm: () => void
}): ReactElement {
  return (
    <LegacyModalSurface onClose={onClose} title="Delete template?" size="sm">
      <div className="ka-modal is-sm">
        <header className="ka-modal-head">
          <h2 className="ka-modal-head-title">Delete template?</h2>
        </header>
        <div className="ka-modal-body">
          <p>
            This permanently deletes <strong>{template.name}</strong> and all of its
            account rows. This can&rsquo;t be undone.
          </p>
          {serverError ? (
            <div className="ka-banner is-error" role="alert" aria-live="assertive" style={{ marginTop: 12 }}>
              {serverError}
            </div>
          ) : null}
        </div>
        <footer className="ka-modal-foot">
          <button type="button" className="ka-btn ka-btn--ghost" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </button>
          <button
            type="button"
            className="ka-btn ka-btn--destructive"
            onClick={onConfirm}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Deleting…' : 'Delete Template'}
          </button>
        </footer>
      </div>
    </LegacyModalSurface>
  )
}

function errorToMessage(cause: unknown): string {
  const appError = cause as AppError
  return appError?.message ?? 'Something went wrong. Please try again.'
}
